import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { X, Maximize2, Minimize2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Fullscreen course viewer. Serves the paid-gated Rise export from
 * `/api/courses/<product_id>/index.html` inside an iframe. The iframe's
 * relative asset requests hit the same auth-checked endpoint.
 */
export default function CourseViewer() {
  const { productId } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [state, setState] = useState("loading"); // loading | ok | 403 | 404
  const [fs, setFs] = useState(false);

  useEffect(() => {
    api.get(`/products/${productId}`).then((r) => setProduct(r.data)).catch(() => setState("404"));
  }, [productId]);

  useEffect(() => {
    if (user === null || !product) return;                // still resolving
    if (user === false) { navigate(`/login?next=/course/${productId}`); return; }
    // Do a HEAD-like check — hit index.html directly. If we get 200, we're in.
    api.get(`/courses/${productId}/index.html`, { responseType: "blob", validateStatus: () => true })
      .then((r) => {
        if (r.status === 200) setState("ok");
        else if (r.status === 403) setState("403");
        else setState("404");
      });
  }, [user, product, productId, navigate]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  // Propagate `#/lessons/...` from the parent URL into the iframe so
  // "Open in new tab" from CourseModal lands on the same slide.
  const initialHash = typeof window !== "undefined" ? window.location.hash : "";
  const iframeSrc = `${backendUrl}/api/courses/${productId}/index.html${initialHash || ""}`;

  if (state === "loading") {
    return <div className="min-h-screen grid place-items-center bg-[var(--cream)]"><p className="text-[var(--muted)]">Loading course…</p></div>;
  }
  if (state === "403") {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--cream)] p-6">
        <div className="max-w-md text-center border border-[var(--line)] bg-white p-10" data-testid="course-403">
          <AlertCircle className="text-red-600 mx-auto" size={32} />
          <h1 className="font-display text-3xl text-[var(--navy)] mt-4">Access denied</h1>
          <p className="text-sm text-[var(--muted)] mt-3">
            This course is only available to enrolled students. Purchase it to unlock.
          </p>
          <Link to={`/academy/${productId}`} className="btn btn-navy inline-flex mt-6">Go to product page</Link>
        </div>
      </div>
    );
  }
  if (state === "404") {
    return (
      <div className="min-h-screen grid place-items-center bg-[var(--cream)] p-6">
        <div className="max-w-md text-center border border-[var(--line)] bg-white p-10">
          <h1 className="font-display text-3xl text-[var(--navy)]">Course not published</h1>
          <p className="text-sm text-[var(--muted)] mt-3">
            The content for this course hasn't been uploaded yet. Please check back later.
          </p>
          <Link to="/dashboard" className="btn btn-navy inline-flex mt-6">Back to dashboard</Link>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-black ${fs ? "fixed inset-0 z-50" : "min-h-screen flex flex-col"}`} data-testid="course-viewer">
      <div className="flex items-center justify-between px-4 py-2 bg-[var(--navy)] text-white text-sm">
        <button onClick={() => navigate("/dashboard")} className="flex items-center gap-1.5 hover:text-[var(--gold)]" data-testid="course-close">
          <X size={14} /> Exit course
        </button>
        <span className="font-medium truncate max-w-[60vw]">{product?.name}</span>
        <button onClick={() => setFs((v) => !v)} className="flex items-center gap-1.5 hover:text-[var(--gold)]" data-testid="course-fullscreen">
          {fs ? <><Minimize2 size={14} /> Exit fullscreen</> : <><Maximize2 size={14} /> Fullscreen</>}
        </button>
      </div>
      <iframe
        src={iframeSrc}
        title={product?.name || "Course"}
        className="flex-1 w-full border-0"
        style={{ minHeight: fs ? "calc(100vh - 40px)" : "calc(100vh - 40px)" }}
        allow="autoplay; fullscreen; encrypted-media"
        data-testid="course-iframe"
      />
    </div>
  );
}
